import { useMutation } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';

import {
  HourlyTuitionBatchOutcome,
  HourlyTuitionDiscountType,
  HourlyTuitionPostStudentStatus,
} from './hourly-tuition.constants';
import {
  buildChunkRequest,
  chunkStudents,
  HOURLY_TUITION_POST_CHUNK_SIZE,
  mergeChunkedPostResponses,
} from './post-tuition-chunks';

export interface PostTuitionStudent {
  studentId: string;
  discountType: HourlyTuitionDiscountType;
  discount: number;
}

export interface PostTuitionRequest {
  sessionId: string;
  fromDate: string;
  toDate: string;
  students: Array<PostTuitionStudent>;
  isScheduledPayment?: boolean;
  /** V2 only: post PENDING invoices with no charge (ENR-3102). */
  invoiceOnly?: boolean;
  scheduledDate?: string;
  description?: string;
}

export type PostTuitionStudentStatus = HourlyTuitionPostStudentStatus;

export type PostTuitionBatchOutcome = HourlyTuitionBatchOutcome;

export interface StudentResult {
  studentId: string;
  studentName?: string;
  invoiceId?: string | null;
  paymentResult?: {
    status: string;
    paymentIntentId: string;
  } | null;
  totalAmount?: number;
  discountApplied?: number;
  finalAmount?: number;
  finalAmountWithFees?: number;
  status: PostTuitionStudentStatus;
  message?: string;
  error?: string;
}

export interface PostTuitionResponse {
  success: boolean;
  outcome?: PostTuitionBatchOutcome;
  processedStudents: number;
  results: Array<StudentResult>;
  message?: string;
}

type PostTuitionTransport = (
  data: PostTuitionRequest,
) => Promise<PostTuitionResponse>;

/** V1 — hourly-tuition-billing/post-tuition (captureManualPayment). */
export const postTuitionV1API = async (
  data: PostTuitionRequest,
): Promise<PostTuitionResponse> => {
  const response = await baseAPI.post<PostTuitionResponse>(
    '/hourly-tuition-billing/post-tuition',
    data,
  );

  return response.data;
};

/** V2 — PaymentService.settle + SubscriptionInvoice. */
export const postTuitionV2API = async (
  data: PostTuitionRequest,
): Promise<PostTuitionResponse> => {
  const response = await baseAPI.post<PostTuitionResponse>(
    '/v2/hourly-tuitions',
    data,
  );

  return response.data;
};

/**
 * ENR-3572: large cohorts (e.g. RDD ~300+) exceed the gateway request
 * deadline when posted in one HTTP call. Chunk so each request stays short;
 * BE already persists posting history per student, so chunks are safe.
 */
export const postTuitionInChunks = async (
  data: PostTuitionRequest,
  transport: PostTuitionTransport,
  chunkSize: number = HOURLY_TUITION_POST_CHUNK_SIZE,
): Promise<PostTuitionResponse> => {
  const chunks = chunkStudents(data.students, chunkSize);
  if (chunks.length <= 1) {
    return transport(data);
  }

  const responses: Array<PostTuitionResponse> = [];
  for (const students of chunks) {
    responses.push(await transport(buildChunkRequest(data, students)));
  }
  return mergeChunkedPostResponses(responses);
};

export const postTuitionAPI = async (
  data: PostTuitionRequest,
  isV2: boolean,
): Promise<PostTuitionResponse> =>
  postTuitionInChunks(data, isV2 ? postTuitionV2API : postTuitionV1API);

export interface PostTuitionMutationInput {
  data: PostTuitionRequest;
  isV2: boolean;
}

export const usePostTuition = () => {
  return useMutation({
    mutationFn: ({ data, isV2 }: PostTuitionMutationInput) =>
      postTuitionAPI(data, isV2),
  });
};
